/* eslint-disable no-param-reassign */

'use client';

import type { MutableRefObject } from 'react';
import { useEffect, useRef } from 'react';
import type { Key } from 'react-aria';
import type { LayoutInfo, Rect, ScrollPadding } from '../layouts/types';
import type { ListFilterState } from '../List/types';
import { useCachedValue } from './useCachedValue';
import type { OnScrollCompleteCallback, ScrollAlignment } from './useScrollTo';
import {
  getScrollPaddingTop,
  getScrollTopForAlignment,
  isItemInView,
} from './useScrollTo';

type AlignmentOptions = ScrollAlignment | 'auto';

type UseSmoothScrollProps = {
  layoutInfo: Map<Key, LayoutInfo>;
  visibleRect: Rect;
  onScrollComplete?: OnScrollCompleteCallback;
  scrollPadding?: ScrollPadding;
  rafRef?: MutableRefObject<number | null>;
};

type Options = {
  force?: boolean;
  ease?: (t: number) => number;
  duration?: number;
};

/**
 * A hook that provides a function to smoothly scroll to a key. When called
 * multiple times during a scroll, it will cancel the previous scroll and
 * start a new one.
 *
 * @example
 * const scrollTo = useSmoothScroll(
 *   ref, props, state
 * );
 *
 * function onClick() {
 *   scrollTo(key);
 * }
 */

export function useSmoothScroll<T>(
  scrollRef: React.RefObject<HTMLElement | null>,
  {
    layoutInfo,
    visibleRect,
    onScrollComplete,
    scrollPadding = { top: 0, bottom: 0 },
    rafRef,
  }: UseSmoothScrollProps,
  state?: ListFilterState<T>
) {
  const internalRafRef = useRef<number | null>(null);
  const raf = rafRef || internalRafRef;

  // Clean up RAF on unmount
  useEffect(() => {
    return () => {
      if (raf.current) {
        cancelAnimationFrame(raf.current);
      }
    };
  }, [raf]);

  const cache = useCachedValue({ layoutInfo, visibleRect });
  const collection = state?.collection;

  /**
   * Smoothly scrolls to the given key.
   * @param alignmentProp
   */

  return (
    key: Key,
    alignmentProp: AlignmentOptions = 'auto',
    {
      force = false,
      ease = easeInOutQuint,
      duration: fixedDuration,
    }: Options = {}
  ) => {
    function onComplete(didScroll: boolean) {
      onScrollComplete?.({
        didScroll,
        node: collection?.getItem(key)?.value,
        key,
        // @ts-expect-error FIXME(strictNullChecks)
        element: scrollRef.current.querySelector(`[data-key="${key}"]`),
        type: 'smooth',
      });
    }

    // @ts-expect-error FIXME(strictNullChecks)
    cancelAnimationFrame(raf.current);
    raf.current = null;

    // @ts-expect-error FIXME(strictNullChecks)
    const layout = cache.current.layoutInfo.get(key);

    if (!layout) {
      // eslint-disable-next-line no-console
      console.warn(`Attempting to scroll to missing key: ${key}`);
      return;
    }

    const { rect: targetRect } = layout;

    // If the item is already in view, don't scroll.
    if (!force && isItemInView(key, layoutInfo, visibleRect, scrollPadding)) {
      onComplete(false);
      return;
    }

    let alignment = alignmentProp;

    if (alignment === 'auto') {
      alignment =
        targetRect.y - getScrollPaddingTop(key, layoutInfo) <= visibleRect.y
          ? 'start'
          : 'end';
    }

    const initialOffset = getScrollTopForAlignment(
      key,
      alignment as ScrollAlignment,
      // @ts-expect-error FIXME(strictNullChecks)
      cache.current.layoutInfo,
      // @ts-expect-error FIXME(strictNullChecks)
      cache.current.visibleRect,
      // @ts-expect-error FIXME(strictNullChecks)
      scrollRef.current.scrollHeight,
      scrollPadding
    );

    // @ts-expect-error FIXME(strictNullChecks)
    const startPosition = scrollRef.current.scrollTop;
    const startTime = Date.now();
    const duration =
      fixedDuration ?? getScrollDuration(initialOffset - startPosition);

    function run() {
      const now = Date.now();
      const elapsed = now - startTime;
      const progress = ease(Math.min(elapsed / duration, 1));
      const offset = getScrollTopForAlignment(
        key,
        alignment as ScrollAlignment,
        // @ts-expect-error FIXME(strictNullChecks)
        cache.current.layoutInfo,
        // @ts-expect-error FIXME(strictNullChecks)
        cache.current.visibleRect,
        // @ts-expect-error FIXME(strictNullChecks)
        scrollRef.current.scrollHeight,
        scrollPadding
      );

      const interpolated = startPosition + (offset - startPosition) * progress;

      if (elapsed < duration) {
        // @ts-expect-error FIXME(strictNullChecks)
        scrollRef.current.scrollTop = interpolated;
        raf.current = requestAnimationFrame(run);
      } else {
        // @ts-expect-error FIXME(strictNullChecks)
        scrollRef.current.scrollTop = offset;
        raf.current = null;
        onComplete(true);
      }
    }

    raf.current = requestAnimationFrame(run);
  };
}

function easeInOutQuint(t: number) {
  // eslint-disable-next-line no-plusplus, no-param-reassign
  return t < 0.5 ? 16 * t * t * t * t * t : 1 + 16 * --t * t * t * t * t;
}

/**
 * Determine a variable scroll duration based upon the distance
 * to scroll.
 */

function getScrollDuration(
  distance: number,
  minDuration = 600,
  maxDuration = 1000,
  durationPerPixel = 0.5
) {
  const initialDuration = Math.abs(distance) * durationPerPixel;
  const clampedDuration = Math.min(
    maxDuration,
    Math.max(minDuration, initialDuration)
  );
  return clampedDuration;
}
